import React from 'react';
import { Check, Info, Sparkles } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
import { NeedsFlag } from '../ui/NeedsFlag';
type ReadyClaim = {
  name: string;
  rationale: string;
};
type RelativeClaim = {
  name: string;
  rationale: string;
  needs: string;
};
type HealthClaim = {
  name: string;
  nutrient: string;
};
const readyClaims: ReadyClaim[] = [{
  name: 'Good source of vitamin D',
  rationale: 'Provides 2.7 µg per 20 g serving, 28% of the RDI, meeting the Schedule 4 condition of ≥ 25% RDI.'
}, {
  name: 'Good source of protein (as prepared)',
  rationale: 'Provides 10.0 g per serving as prepared with 200 mL skim milk, meeting the Schedule 4 condition of ≥ 10 g per serving.'
}];
const comparativeNeeds = 'Needs: a reference food and a claim direction';
const referenceFoodNeeds = 'Needs: a reference food';
const relativeClaims: RelativeClaim[] = [{
  name: 'Energy',
  rationale: "Energy met a relative threshold of ≥ 25%, but a comparative nutrition content claim requires a reference food and a stated direction, for example 'reduced energy'.",
  needs: comparativeNeeds
}, {
  name: 'Sodium',
  rationale: "Sodium met a relative threshold of ≥ 25%, but a comparative nutrition content claim requires a reference food and a stated direction, for example 'reduced sodium'.",
  needs: comparativeNeeds
}, {
  name: 'Sugars',
  rationale: "Sugars met a relative threshold of ≥ 25%, but a comparative nutrition content claim requires a reference food and a stated direction, for example 'reduced sugar'.",
  needs: comparativeNeeds
}, {
  name: 'Diet',
  rationale: "'Diet' is a relative nutrition content claim; it requires a comparison against a reference food to substantiate.",
  needs: referenceFoodNeeds
}, {
  name: 'No added Sodium',
  rationale: "'No added Sodium' is a relative nutrition content claim; it requires a comparison against a reference food to substantiate.",
  needs: referenceFoodNeeds
}, {
  name: 'No added Sugars',
  rationale: "'No added Sugars' is a relative nutrition content claim; it requires a comparison against a reference food to substantiate.",
  needs: referenceFoodNeeds
}];
const healthClaims: HealthClaim[] = [{
  name: 'Necessary for normal teeth and bone structure',
  nutrient: 'Calcium'
}, {
  name: 'Enhances bone mineral density',
  nutrient: 'Calcium'
}, {
  name: 'Contributes to the regulation of hormonal activity',
  nutrient: 'Vitamin B6'
}, {
  name: 'Contributes to the normal function of digestive enzymes',
  nutrient: 'Calcium'
}, {
  name: 'Contributes to normal energy metabolism',
  nutrient: 'Calcium'
}, {
  name: 'Reduces risk of osteoporosis',
  nutrient: 'Calcium'
}, {
  name: 'Reduces risk of osteoporotic fracture',
  nutrient: 'Calcium'
}, {
  name: 'Necessary for normal nerve and muscle function',
  nutrient: 'Calcium'
}, {
  name: 'Necessary for normal blood coagulation',
  nutrient: 'Calcium'
}, {
  name: 'Contributes to normal cell division',
  nutrient: 'Calcium'
}, {
  name: 'Contributes to normal growth and development',
  nutrient: 'Calcium'
}, {
  name: 'Contributes to normal energy production',
  nutrient: 'Iron'
}, {
  name: 'Necessary for normal immune system function',
  nutrient: 'Iron'
}, {
  name: 'Contributes to normal blood formation',
  nutrient: 'Iron'
}, {
  name: 'Contributes to normal cognitive function',
  nutrient: 'Iron'
}, {
  name: 'Contributes to the reduction of tiredness and fatigue',
  nutrient: 'Iron'
}, {
  name: 'Contributes to normal growth and development (Children)',
  nutrient: 'Iron'
}, {
  name: 'Necessary for normal cell division',
  nutrient: 'Iron'
}, {
  name: 'Necessary for normal protein metabolism',
  nutrient: 'Vitamin B6'
}, {
  name: 'Necessary for normal iron transport and metabolism',
  nutrient: 'Vitamin B6'
}, {
  name: 'Contributes to normal cysteine synthesis',
  nutrient: 'Vitamin B6'
}, {
  name: 'Contributes to normal functioning of the nervous system',
  nutrient: 'Vitamin B6'
}, {
  name: 'Contributes to normal homocysteine metabolism',
  nutrient: 'Vitamin B6'
}, {
  name: 'Contributes to normal glycogen metabolism',
  nutrient: 'Vitamin B6'
}, {
  name: 'Contributes to normal psychological function',
  nutrient: 'Vitamin B6'
}, {
  name: 'Contributes to normal red blood cell formation',
  nutrient: 'Vitamin B6'
}, {
  name: 'Contributes to normal immune system function',
  nutrient: 'Vitamin B6'
}, {
  name: 'Necessary for normal neurological structure and function',
  nutrient: 'vitamin B12'
}, {
  name: 'Contributes to normal cognitive development (Children)',
  nutrient: 'Iron'
}, {
  name: 'Necessary for normal neurological development in the foetus',
  nutrient: 'Iron'
}, {
  name: 'Necessary for normal oxygen transport',
  nutrient: 'Iron'
}, {
  name: 'Calcium enhances bone mineral density',
  nutrient: 'Calcium'
}, {
  name: 'Calcium reduces risk of osteoporosis (Persons 65 years and over)',
  nutrient: 'Calcium'
}, {
  name: 'Calcium reduces risk of osteoporosis',
  nutrient: 'Calcium'
}, {
  name: 'Calcium reduces risk of osteoporotic fracture',
  nutrient: 'Calcium'
}, {
  name: 'Iron (general level health claim)',
  nutrient: 'Iron'
}, {
  name: 'Contributes to normal cognitive development',
  nutrient: 'Iron'
}];
const needsCount = relativeClaims.length + healthClaims.length;
const onLabelClaims = ['LOW GI = 36 (LOW) MADE WITH SKIM MILK', 'MADE WITH SKIM MILK', 'CONTAINS GLUTEN, MILK, SOY', 'TOTAL EXTRACT 38%'];
export function FoodSuggestedClaimsTab({
  onClaimsClick
}: {
  onClaimsClick?: () => void;
}) {
  return <div className="space-y-5" data-id="element-401" data-mp="1er5g4m7louo3">
      <header className="flex items-start gap-3" data-id="element-402" data-mp="19u5b8emhlxyk">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-403" data-mp="19tcd221lggs1">
          <Sparkles className="h-5 w-5" data-id="element-404" data-mp="1okutg0yd8dbx" />
        </span>
        <div data-id="element-405" data-mp="1jkq9u225v2hh">
          <h2 className="font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-406" data-mp="1jk71nd2bbsgn">Suggested claims</h2>
          <p className="mt-1 text-sm leading-relaxed text-[#6b7d70]" data-id="element-407" data-mp="1xjznatyzfihr">Claims this product may be eligible to make, based on its composition.</p>
          <p className="mt-2 font-mono text-xs font-semibold uppercase tracking-[0.12em] text-[#748477]" data-id="element-408" data-mp="20l2vsyj4f26y">
            {readyClaims.length} ready · {needsCount} need substantiation
          </p>
        </div>
      </header>

      <section aria-labelledby="ready-heading" data-id="element-409" data-mp="2aipelw3jjdw7">
        <h3 id="ready-heading" className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#708174]" data-id="element-410" data-mp="2ahxnaxgz82bq">
          <span className="h-2 w-2 rounded-full bg-forest-green" data-id="element-411" data-mp="2jcc3d29bzgvw" />
          Ready to use · {readyClaims.length}
        </h3>
        <ul className="mt-3 divide-y divide-[#edf0eb] overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-412" data-mp="2o9c899ug2dlf">
          {readyClaims.map(claim => <li key={claim.name} className="px-5 py-4" data-id="element-413" data-mp="2y9cub3jp77tb">
              <div className="flex flex-wrap items-start justify-between gap-2" data-id="element-414" data-mp="2y6qob8cgh0d7">
                <p className="text-sm font-medium text-evergreen" data-id="element-415" data-mp="yxa8hmnlq1ch">{claim.name}</p>
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#8b968d]" data-id="element-416" data-mp="13vle1l6v5pow">Nutrition</span>
              </div>
              <p className="mt-1 text-sm leading-relaxed text-[#718174]" data-id="element-417" data-mp="3ced8v4xeg0ld">{claim.rationale}</p>
              <ClaimCitation data-id="element-418" data-mp="3ccil7iuqtudl" />
            </li>)}
        </ul>
      </section>

      <section aria-labelledby="needs-heading" data-id="element-419" data-mp="38annspfxo153">
        <h3 id="needs-heading" className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#708174]" data-id="element-420" data-mp="33egfiq02edjz">
          <span className="h-2 w-2 rounded-full bg-amber-500" data-id="element-421" data-mp="3wfno5xchm2z" />
          Needs substantiation · {needsCount}
        </h3>
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900" data-id="element-422" data-mp="3r39gj0vjnblg">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" data-id="element-423" data-mp="3m3n0yks8tj9h" />
          All {healthClaims.length} health claims below are blocked by the NPSC score of 15. Reduce the score to 4.0 or below to unlock them.
        </div>
        <ul className="mt-3 divide-y divide-[#edf0eb] overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-424" data-mp="2ar2pzcyp4k9w">
          {relativeClaims.map(claim => <li key={claim.name} className="px-5 py-4" data-id="element-425" data-mp="jmj0c16hkvqf">
              <div className="flex flex-wrap items-start justify-between gap-2" data-id="element-426" data-mp="4nxeua9t1lv5">
                <p className="text-sm font-medium text-evergreen" data-id="element-427" data-mp="9ms49v2a28o8">{claim.name}</p>
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#8b968d]" data-id="element-428" data-mp="9outwdzipzsf">Nutrition</span>
              </div>
              <p className="mt-1 text-sm leading-relaxed text-[#718174]" data-id="element-429" data-mp="12bpa2ypayd03">{claim.rationale}</p>
              <NeedsFlag className="mt-2" data-id="element-430" data-mp="nn2tktnxvf7a">{claim.needs}</NeedsFlag>
              <ClaimCitation data-id="element-431" data-mp="nkp18skn8h3f" />
            </li>)}
          {healthClaims.map((claim, index) => <li key={`${claim.name}-${claim.nutrient}-${index}`} className="px-5 py-4" data-id="element-432" data-mp="1gb753vt6ymhw" data-mpi={index}>
              <div className="flex flex-wrap items-start justify-between gap-2" data-id="element-433" data-mp="1g9duy6jddk55">
                <p className="text-sm font-medium text-evergreen" data-id="element-434" data-mp="1l38ftdapo55t">{claim.name}</p>
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#8b968d]" data-id="element-435" data-mp="35qrjxlso5kfv">Health</span>
              </div>
              <p className="mt-1 text-sm leading-relaxed text-[#718174]" data-id="element-436" data-mp="3fi5nq5rnl6et">
                Supported by {claim.nutrient}. Conditional: food health claims also require the Nutrient Profiling Scoring Criterion to be met.
              </p>
              <NeedsFlag className="mt-2" data-id="element-437" data-mp="2muja04opole8">Needs: NPSC review</NeedsFlag>
              <p className="mt-1 text-xs text-[#8b968d]" data-id="element-438" data-mp="2hunaahn1rg4h">Supported by: {claim.nutrient}</p>
              <ClaimCitation data-id="element-439" data-mp="2vtwnd8fyhu48" />
            </li>)}
        </ul>
      </section>

      <section className="rounded-2xl border border-[#e2e8e0] bg-[#f6f8f4] p-5" aria-labelledby="food-already-on-label" data-id="element-440" data-mp="2vrtqc4gdz0q0">
        <h3 id="food-already-on-label" className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-441" data-mp="b3eb4l26ov62">
          Already on your label · confirmed valid
        </h3>
        <ul className="mt-3 divide-y divide-[#e5eae2]" data-id="element-442" data-mp="3eevqfxyifeq6">
          {onLabelClaims.map(claim => <li key={claim} className="flex flex-wrap items-center justify-between gap-2 py-2" data-id="element-443" data-mp="3ads8n5wwj8mb">
              <span className="text-sm text-[#5d6f62]" data-id="element-444" data-mp="35mk2qjus65dw">{claim}</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-forest-green/10 px-2 py-0.5 text-xs font-semibold text-forest-green" data-id="element-445" data-mp="35kpeowo7px5l">
                <Check className="h-3 w-3" data-id="element-446" data-mp="2vow3bgm8c9pe" />
                Confirmed valid
              </span>
            </li>)}
        </ul>
        <button type="button" onClick={onClaimsClick} className="mt-3 text-xs font-semibold text-[#637667] transition hover:text-forest-green hover:underline" data-id="element-447" data-mp="2qo85oc3hwum7">
          View full assessment → Claims tab
        </button>
      </section>

      <p className="text-sm leading-relaxed text-[#8b968d]" data-id="element-448" data-mp="kv9uikji16ox">
        Suggestions only — not a verification. Each claim must be confirmed and, where indicated, substantiated before use.
      </p>
    </div>;
}
function ClaimCitation() {
  return <div className="mt-3" data-id="element-449" data-mp="sg3yg3ebkobu">
      <RegulationLink text="Standard 1.2.7 Nutrition, health and related claims" href="https://www.legislation.gov.au/F2015L00394/latest/text" data-id="element-450" data-mp="sfc22qcwemki" />
    </div>;
}